import React from 'react';
import { Helmet } from 'react-helmet-async';
import Header from '../components/Header';
import Footer from '../components/Footer';

const ANVIL_URL =
  'https://app.useanvil.com/weld/equipease-lease-co/application-form-businessloan';

const handleApplyClick = () => {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'conversion', {
      send_to: 'AW-11080823629/ihQUCJ6gr-MbEM3m36Mp',
      value: 400.0,
      currency: 'CAD',
    });
    window.gtag('event', 'business_loan_apply_click');
  }
  if (typeof window.fbq === 'function') {
    window.fbq('track', 'Lead', { content_name: 'Business Loan Application' });
  }
};

const features = [
  {
    title: 'Up to $500,000',
    desc: 'Working capital lines sized to your business — from $10K to half a million.',
  },
  {
    title: 'Funded in 48 Hours',
    desc: 'Most approvals fund within two business days of signed offer.',
  },
  {
    title: 'No Collateral Required',
    desc: 'Unsecured lending — your equipment and assets stay yours.',
  },
  {
    title: 'Flexible Terms',
    desc: '3 to 18 month payback schedules with daily or weekly remittance.',
  },
  {
    title: 'Soft Credit Pull',
    desc: 'Initial quote uses a soft inquiry — no impact on your credit score.',
  },
  {
    title: 'Bad Credit? OK.',
    desc: 'We work with scores as low as 500 when revenue supports the loan.',
  },
];

const steps = [
  {
    n: '01',
    title: 'Apply Online',
    desc: 'Complete the secure 5-minute application — no document upload required to start.',
  },
  {
    n: '02',
    title: 'Get a Quote',
    desc: 'Our team reviews your business profile and presents tailored offers within hours.',
  },
  {
    n: '03',
    title: 'Receive Funds',
    desc: 'Sign electronically and funds are wired direct to your business account in 48 hours.',
  },
];

const BusinessLoansPage = () => {
  return (
    <div className="min-h-screen flex flex-col" data-testid="business-loans-page">
      <Helmet>
        <title>Business Loans Canada — Fast Unsecured Funding | EquipEASE</title>
        <meta
          name="description"
          content="Get an unsecured business loan up to $500,000 with funds in as little as 48 hours. No collateral required. Apply online with EquipEASE Lease Co."
        />
        <link rel="canonical" href="https://equipease.ca/businessloans" />
        <meta property="og:title" content="Business Loans Canada — Fast Unsecured Funding | EquipEASE" />
        <meta
          property="og:description"
          content="Unsecured Canadian business loans up to $500K, funded in 48 hours. No collateral, flexible terms."
        />
        <meta property="og:url" content="https://equipease.ca/businessloans" />
      </Helmet>

      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#1a1a2e] text-white py-20 md:py-28">
        <div className="absolute inset-0 opacity-10 pointer-events-none">
          <div className="absolute top-0 left-0 w-72 h-72 bg-[#FF6600] rounded-full blur-[120px]" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#FF9933] rounded-full blur-[150px]" />
        </div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block bg-[#FF6600]/20 text-[#FF9933] text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-5">
              Fast Cash for Your Business
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold leading-tight mb-6">
              Business Loans —<br />
              <span className="text-[#FF6600]">Funded in 48 Hours</span>
            </h1>
            <p className="text-lg md:text-xl text-gray-300 mb-8 leading-relaxed max-w-2xl">
              No collateral. No red tape. Unsecured working capital from{' '}
              <span className="text-white font-semibold">$10,000 to $500,000</span> for Canadian
              businesses ready to grow, hire, or bridge a gap.
            </p>
            <a
              href={ANVIL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={handleApplyClick}
              data-testid="business-loan-hero-cta"
              className="group inline-flex items-center bg-[#FF6600] hover:bg-[#FF9933] text-white px-8 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-[#FF6600]/30 hover:shadow-[#FF6600]/50 hover:-translate-y-0.5"
            >
              Apply for a Business Loan
              <svg
                className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M14 5l7 7m0 0l-7 7m7-7H3"
                />
              </svg>
            </a>
            <p className="text-gray-400 text-sm mt-4">
              Soft credit check only — no impact on your score.
            </p>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-3">
              Why Canadian Operators Choose EquipEASE
            </h2>
            <div className="w-20 h-1 bg-[#FF6600] mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {features.map((f) => (
              <div
                key={f.title}
                className="bg-white border border-gray-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-0.5 transition-all"
              >
                <div className="w-12 h-12 bg-[#FF6600]/10 text-[#FF6600] rounded-xl flex items-center justify-center mb-4">
                  <svg
                    className="w-6 h-6"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      d="M5 13l4 4L19 7"
                    />
                  </svg>
                </div>
                <h3 className="text-lg font-bold text-[#333333] mb-2">{f.title}</h3>
                <p className="text-gray-600 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-16 md:py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12 max-w-2xl mx-auto">
            <h2 className="text-3xl md:text-4xl font-bold text-[#333333] mb-3">
              How It Works
            </h2>
            <div className="w-20 h-1 bg-[#FF6600] mx-auto" />
          </div>
          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {steps.map((s) => (
              <div key={s.n} className="text-left">
                <div className="text-5xl font-extrabold text-[#FF6600]/30 mb-3">{s.n}</div>
                <h3 className="text-xl font-bold text-[#333333] mb-2">{s.title}</h3>
                <p className="text-gray-600 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Eligibility */}
      <section className="py-16 md:py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto bg-white rounded-2xl shadow-lg p-8 md:p-12">
            <h2 className="text-2xl md:text-3xl font-bold text-[#333333] mb-6">
              Basic Eligibility
            </h2>
            <ul className="space-y-4 text-gray-700">
              {[
                'Canadian business with a valid CRA business number',
                'Minimum 6 months in business',
                'Monthly revenue of $10,000 or more',
                'Active business chequing account',
                'Owner is a Canadian resident, 18+',
              ].map((item) => (
                <li key={item} className="flex items-start">
                  <svg
                    className="w-6 h-6 text-[#FF6600] mt-0.5 mr-3 flex-shrink-0"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-16 md:py-20 bg-[#1a1a2e] text-white">
        <div className="container mx-auto px-4 text-center max-w-2xl">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Funded?
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Five-minute application. Funds in 48 hours. No surprises.
          </p>
          <a
            href={ANVIL_URL}
            target="_blank"
            rel="noopener noreferrer"
            onClick={handleApplyClick}
            data-testid="business-loan-footer-cta"
            className="inline-flex items-center bg-[#FF6600] hover:bg-[#FF9933] text-white px-10 py-4 rounded-xl font-bold text-lg transition-all shadow-lg shadow-[#FF6600]/30 hover:shadow-[#FF6600]/50 hover:-translate-y-0.5"
          >
            Apply for a Business Loan
            <svg
              className="w-5 h-5 ml-2"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M14 5l7 7m0 0l-7 7m7-7H3"
              />
            </svg>
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default BusinessLoansPage;
